import { Button, Checkbox, DatePicker, Form, Input, Modal, Radio, Select, Space, Switch, TimePicker } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import React from 'react';
import { PricingAPI } from '../../../../../controller/PricingAPI';
import { departmentOptionsFor, showRequestError } from '../todoHelpers';

const WEEKDAYS = [
    { label: '一', value: 1 },
    { label: '二', value: 2 },
    { label: '三', value: 3 },
    { label: '四', value: 4 },
    { label: '五', value: 5 },
    { label: '六', value: 6 },
    { label: '日', value: 7 },
];

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1} 日`, value: i + 1 }));

// 對應後端 TodoTemplate::CHECKLIST_MAX；兩邊要一起改，否則超過時使用者會拿到英文的驗證訊息
const CHECKLIST_MAX = 50;

// 指派到人與指派到部門二選一。在前端就讓它成立，不要讓使用者靠 422 才知道 ——
// 後端仍然會擋，這裡只是不讓人白送一次。
const TARGET_USER = 'user';
const TARGET_DEPARTMENT = 'department';

function TodoTemplateFormModal({ open, template, onClose, onSaved }) {
    const [form] = Form.useForm();
    const [saving, setSaving] = React.useState(false);
    const [users, setUsers] = React.useState([]);
    const [departments, setDepartments] = React.useState([]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        PricingAPI.users.options().then((response) => setUsers(response.data)).catch(showRequestError);
        PricingAPI.departments.options().then((response) => setDepartments(response.data)).catch(showRequestError);
    }, [open]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const t = template;
        form.setFieldsValue({
            subject: t ? t.subject : '',
            description: t ? t.description : '',
            target: t && t.department_id ? TARGET_DEPARTMENT : TARGET_USER,
            assignee_id: t ? t.assignee_id : null,
            department_id: t ? t.department_id : null,
            recurrence: t ? t.recurrence : 'daily',
            recurrence_days: t ? t.recurrence_days || [] : [],
            start_time: t && t.start_time ? moment(t.start_time, 'HH:mm:ss') : null,
            effective_from: t && t.effective_from ? moment(t.effective_from, 'YYYY-MM-DD') : moment(),
            active: t ? t.active : true,
            checklist: t && t.checklist ? t.checklist : [],
        });
    }, [open, template, form]);

    const handleOk = () => {
        form.validateFields().then((values) => {
            setSaving(true);
            const payload = {
                subject: values.subject,
                description: values.description || null,
                // 二選一：沒選中的那一邊一律送 null，不要把舊值留著
                assignee_id: values.target === TARGET_USER ? values.assignee_id : null,
                department_id: values.target === TARGET_DEPARTMENT ? values.department_id : null,
                recurrence: values.recurrence,
                recurrence_days: values.recurrence === 'daily' ? [] : (values.recurrence_days || []),
                start_time: values.start_time ? values.start_time.format('HH:mm') : null,
                effective_from: values.effective_from.format('YYYY-MM-DD'),
                active: !!values.active,
                checklist: (values.checklist || []).filter((label) => (label || '').trim() !== ''),
            };
            const request = template && template.id
                ? PricingAPI.todoTemplates.update(template.id, payload)
                : PricingAPI.todoTemplates.store(payload);
            request.then(() => {
                onSaved();
            }).catch(showRequestError).finally(() => setSaving(false));
        }).catch(() => {});
    };

    return (
        <Modal
            title={template && template.id ? '修改例行工作' : '新增例行工作'}
            visible={open}
            onOk={handleOk}
            onCancel={onClose}
            confirmLoading={saving}
            okText="送出"
            cancelText="取消"
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item name="subject" label="主旨" rules={[{ required: true, message: '請輸入主旨' }, { max: 200 }]}>
                    <Input maxLength={200} />
                </Form.Item>
                <Form.Item name="description" label="描述" extra="做法說明；顯示在展開區">
                    <Input.TextArea rows={3} />
                </Form.Item>
                <Form.Item name="target" label="指派方式">
                    <Radio.Group
                        optionType="button"
                        options={[
                            { label: '指派到人', value: TARGET_USER },
                            { label: '指派到部門', value: TARGET_DEPARTMENT },
                        ]}
                    />
                </Form.Item>
                <Form.Item shouldUpdate={(prev, next) => prev.target !== next.target} noStyle>
                    {({ getFieldValue }) => (getFieldValue('target') === TARGET_DEPARTMENT ? (
                        <Form.Item
                            name="department_id"
                            label="部門"
                            rules={[{ required: true, message: '請選擇部門' }]}
                            extra="該部門成員都會在「待我處理」看到它，誰按「開始」就是誰的"
                        >
                            <Select options={departmentOptionsFor(departments, template)} />
                        </Form.Item>
                    ) : (
                        <Form.Item name="assignee_id" label="負責人" rules={[{ required: true, message: '請選擇負責人' }]}>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                options={users.map((u) => ({ value: u.id, label: u.name }))}
                            />
                        </Form.Item>
                    ))}
                </Form.Item>
                <Form.Item name="recurrence" label="重複規則" extra="已經有人執行過的例行工作不能再改這一項">
                    <Radio.Group
                        optionType="button"
                        options={[
                            { label: '每天', value: 'daily' },
                            { label: '每週', value: 'weekly' },
                            { label: '每月', value: 'monthly' },
                        ]}
                        // 換規則時舊的天數不適用，留著會送出一組對不上的值
                        onChange={() => form.setFieldsValue({ recurrence_days: [] })}
                    />
                </Form.Item>
                <Form.Item shouldUpdate={(prev, next) => prev.recurrence !== next.recurrence} noStyle>
                    {({ getFieldValue }) => {
                        const recurrence = getFieldValue('recurrence');
                        if (recurrence === 'weekly') {
                            return (
                                <Form.Item name="recurrence_days" label="星期" rules={[{ required: true, message: '請至少選一天' }]}>
                                    <Checkbox.Group options={WEEKDAYS} />
                                </Form.Item>
                            );
                        }
                        if (recurrence === 'monthly') {
                            return (
                                <Form.Item
                                    name="recurrence_days"
                                    label="日期"
                                    rules={[{ required: true, message: '請至少選一天' }]}
                                    extra="選 31 號時，沒有 31 號的月份會跳過不產生"
                                >
                                    <Select mode="multiple" options={MONTH_DAYS} />
                                </Form.Item>
                            );
                        }
                        return null;
                    }}
                </Form.Item>
                <Form.Item name="start_time" label="時間" extra="幾點做；決定它在當天清單裡的順序，留空代表整天">
                    <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="effective_from"
                    label="生效起日"
                    rules={[{ required: true, message: '請選生效起日' }]}
                    extra="已經有人執行過的例行工作不能再改重複規則與生效起日 - 那會改寫過去的合規答案。要換規則請停用它並新建一個"
                >
                    <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="檢查清單" extra={`落地時複製成可勾選的項目；留空代表沒有清單；最多 ${CHECKLIST_MAX} 項`}>
                    <Form.List name="checklist">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, ...restField }) => (
                                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 4 }}>
                                        <Form.Item {...restField} noStyle>
                                            <Input maxLength={200} style={{ width: 320 }} placeholder="這一項要做什麼" />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(restField.name)} />
                                    </Space>
                                ))}
                                <Button type="dashed" size="small" disabled={fields.length >= CHECKLIST_MAX} onClick={() => add('')} icon={<PlusOutlined />}>
                                    新增一項
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form.Item>
                <Form.Item name="active" label="啟用" valuePropName="checked" extra="停用後不再產生新的一次，既有的執行紀錄保留">
                    <Switch />
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default TodoTemplateFormModal;
